<template>
  <AppLayout>
    <div class="usine-page">
      <div class="page-header">
        <h1>Usine</h1>
        <div class="page-actions">
          <BaseButton variant="secondary" @click="openExpeditionModal">
            + Nouvelle livraison
          </BaseButton>
          <BaseButton variant="primary" @click="openProductionModal">
            + Production du jour
          </BaseButton>
        </div>
      </div>

      <BaseCard class="filters-card">
        <div class="filters-grid">
          <BaseSelect v-model="selectedProjetId" label="Projet">
            <option value="">-- Sélectionner --</option>
            <option v-for="projet in projets" :key="projet.id" :value="String(projet.id)">
              {{ projet.nom }} ({{ projet.code }})
            </option>
          </BaseSelect>

          <BaseSelect v-model="selectedSiteId" label="Site">
            <option value="">Tous les sites</option>
            <option v-for="site in filteredSites" :key="site.id" :value="String(site.id)">
              {{ site.nom }}
            </option>
          </BaseSelect>

          <BaseInput
            v-model="horizonDate"
            label="Livrable à la date"
            type="date"
          />

          <BaseInput
            v-model.trim="searchText"
            label="Recherche"
            placeholder="Code / désignation"
          />
        </div>
        <div class="horizon-actions">
          <BaseButton variant="ghost" size="sm" @click="setHorizon(1)">Demain</BaseButton>
          <BaseButton variant="ghost" size="sm" @click="setHorizon(3)">+3 jours</BaseButton>
          <BaseButton variant="ghost" size="sm" @click="setHorizon(10)">+10 jours</BaseButton>
        </div>
      </BaseCard>

      <div v-if="error" class="error">{{ error }}</div>

      <div class="kpis-grid">
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.resteAProduire }}</div>
          <div class="kpi-label">A produire</div>
        </BaseCard>
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.enMaturation }}</div>
          <div class="kpi-label">En maturation</div>
        </BaseCard>
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.livrableDate }}</div>
          <div class="kpi-label">Livrable à la date</div>
        </BaseCard>
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.stockUsine }}</div>
          <div class="kpi-label">Stock usine</div>
        </BaseCard>
      </div>

      <section class="panel-section">
        <div class="section-header">
          <h2>Flux usine</h2>
        </div>
        <BaseTable
          :columns="fluxColumns"
          :items="fluxRowsFiltered"
          :loading="loadingFlux"
          :show-actions="false"
        />
      </section>

      <section class="panel-section">
        <div class="section-header">
          <h2>Historique production</h2>
        </div>
        <BaseTable
          :columns="lotColumns"
          :items="lotsFiltered"
          :loading="loadingLots"
          :show-actions="true"
          @view="viewLot"
        >
          <template #cell-dateProduction="{ value }">
            {{ value ? formatDate(value) : '-' }}
          </template>
          <template #cell-projet="{ item }">
            {{ item.projetNom }} ({{ item.projetCode }})
          </template>
          <template #cell-site="{ item }">
            {{ item.siteNom ?? '-' }}
          </template>
          <template #cell-typeElement="{ item }">
            {{ resolveTypeLabel(item.typeElementPrefabriqueId) }}
          </template>
          <template #actions="{ item }">
            <BaseButton variant="ghost" size="sm" @click="viewLot(item)">👁️ Voir</BaseButton>
            <BaseButton variant="ghost" size="sm" @click.stop="openEditLotModal(item)">Modifier</BaseButton>
          </template>
        </BaseTable>
      </section>
    </div>

    <Modal v-if="showProductionModal" @close="closeProductionModal">
      <template #title>{{ editingLot ? 'Modifier lot de production' : 'Renseigner production du jour' }}</template>
      <form class="modal-form" @submit.prevent="saveProduction">
        <BaseSelect v-model="productionForm.projetId" label="Projet *" required>
          <option value="">-- Sélectionner --</option>
          <option v-for="projet in projets" :key="projet.id" :value="String(projet.id)">
            {{ projet.nom }} ({{ projet.code }})
          </option>
        </BaseSelect>

        <BaseSelect v-model="productionForm.siteId" label="Site">
          <option value="">Aucun site</option>
          <option
            v-for="site in productionFormSites"
            :key="site.id"
            :value="String(site.id)"
          >
            {{ site.nom }}
          </option>
        </BaseSelect>

        <BaseSelect v-model="productionForm.typeElementPrefabriqueId" label="Type d'élément *" required>
          <option value="">-- Sélectionner --</option>
          <option v-for="type in typesElements" :key="type.id" :value="String(type.id)">
            {{ type.libelle }} ({{ type.code }})
          </option>
        </BaseSelect>

        <BaseInput
          v-model="productionForm.quantiteProduite"
          label="Quantité produite *"
          type="number"
          placeholder="0"
        />

        <BaseInput
          v-model="productionForm.dureeMaturationJours"
          label="Maturation (jours) *"
          type="number"
          placeholder="0"
          hint="Nécessaire quand la quantité produite augmente."
        />

        <BaseInput
          v-model="productionForm.dateProduction"
          label="Date de production *"
          type="date"
        />

        <div v-if="productionFormError" class="form-error">{{ productionFormError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeProductionModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="savingProduction">
            {{ editingLot ? 'Mettre à jour' : 'Enregistrer' }}
          </BaseButton>
        </div>
      </form>
    </Modal>

    <Modal v-if="showExpeditionModal" @close="closeExpeditionModal">
      <template #title>Créer une livraison</template>
      <form class="modal-form" @submit.prevent="saveExpedition">
        <BaseSelect v-model="expeditionForm.projetId" label="Projet *" required>
          <option value="">-- Sélectionner --</option>
          <option v-for="projet in projets" :key="projet.id" :value="String(projet.id)">
            {{ projet.nom }} ({{ projet.code }})
          </option>
        </BaseSelect>

        <BaseSelect v-model="expeditionForm.siteId" label="Site *" required>
          <option value="">-- Sélectionner --</option>
          <option v-for="site in expeditionFormSites" :key="site.id" :value="String(site.id)">
            {{ site.nom }}
          </option>
        </BaseSelect>

        <BaseInput
          v-model="expeditionForm.dateExpedition"
          label="Date expédition *"
          type="date"
        />

        <BaseInput
          v-model.trim="expeditionForm.idVehicule"
          label="Véhicule"
          placeholder="Immatriculation"
        />

        <BaseInput
          v-model.trim="expeditionForm.nomChauffeur"
          label="Chauffeur"
          placeholder="Nom du chauffeur"
        />

        <BaseSelect v-model="expeditionForm.statut" label="Statut">
          <option value="PLANIFIE">Planifié</option>
          <option value="EN_TRANSIT">En transit</option>
          <option value="LIVRE">Livré</option>
          <option value="PARTIEL">Partiel</option>
          <option value="ANNULE">Annulé</option>
        </BaseSelect>

        <div class="items-header">
          <h3>Items de la livraison</h3>
          <BaseButton type="button" variant="ghost" size="sm" @click="addExpeditionItemRow">
            + Ligne
          </BaseButton>
        </div>

        <div
          v-for="item in expeditionItems"
          :key="item.uid"
          class="item-row"
        >
          <BaseSelect v-model="item.typeElementPrefabriqueId" label="Type d'élément">
            <option value="">-- Sélectionner --</option>
            <option v-for="type in typesElements" :key="type.id" :value="String(type.id)">
              {{ type.libelle }} ({{ type.code }})
            </option>
          </BaseSelect>
          <BaseInput
            v-model="item.quantiteEnvoyee"
            label="Quantité"
            type="number"
            placeholder="0"
          />
          <BaseButton
            type="button"
            variant="ghost"
            size="sm"
            :disabled="expeditionItems.length <= 1"
            @click="removeExpeditionItemRow(item.uid)"
          >
            Supprimer
          </BaseButton>
        </div>

        <div v-if="expeditionFormError" class="form-error">{{ expeditionFormError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeExpeditionModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="savingExpedition">Créer</BaseButton>
        </div>
      </form>
    </Modal>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import AppLayout from '../components/AppLayout.vue';
import BaseButton from '../components/BaseButton.vue';
import BaseCard from '../components/BaseCard.vue';
import BaseInput from '../components/BaseInput.vue';
import BaseSelect from '../components/BaseSelect.vue';
import BaseTable, { type TableColumn } from '../components/BaseTable.vue';
import Modal from '../components/Modal.vue';
import { apiFetch } from '../utils/api';

interface Projet {
  id: number;
  nom: string;
  code: string;
}

interface Site {
  id: number;
  nom: string;
  projetId: number;
}

interface LotProduction {
  id: number;
  projetId: number;
  projetNom?: string;
  projetCode?: string;
  siteId: number | null;
  siteNom?: string | null;
  typeElementPrefabriqueId: number;
  quantiteProduite: number;
  dateProduction: string | null;
}

interface TypeElementPrefabrique {
  id: number;
  code: string;
  libelle: string;
}

interface FluxElement {
  typeElementPrefabriqueId: number;
  type: string;
  designation: string;
  quantiteCommandee: number;
  quantiteProduite: number;
  quantiteEnMaturation: number;
  quantiteStockUsine: number;
  quantiteExpediee: number;
}

interface LivrableProjection {
  typeElementPrefabriqueId: number;
  code: string;
  libelle: string;
  quantiteStockUsine: number;
  quantiteMaturationDisponible: number;
  quantiteLivrable: number;
}

interface ExpeditionItemDraft {
  uid: number;
  typeElementPrefabriqueId: string;
  quantiteEnvoyee: string;
}

interface FluxRow extends FluxElement {
  resteAProduire: number;
  quantiteLivrableDate: number;
}

const router = useRouter();

const projets = ref<Projet[]>([]);
const sites = ref<Site[]>([]);
const typesElements = ref<TypeElementPrefabrique[]>([]);
const lots = ref<LotProduction[]>([]);
const fluxElements = ref<FluxElement[]>([]);
const livrablesProjection = ref<LivrableProjection[]>([]);

const selectedProjetId = ref('');
const selectedSiteId = ref('');
const horizonDate = ref(toDateInput(addDays(new Date(), 1)));
const searchText = ref('');

const loadingLots = ref(false);
const loadingFlux = ref(false);
const error = ref<string | null>(null);

const showProductionModal = ref(false);
const savingProduction = ref(false);
const productionFormError = ref<string | null>(null);
const editingLot = ref<LotProduction | null>(null);

const showExpeditionModal = ref(false);
const savingExpedition = ref(false);
const expeditionFormError = ref<string | null>(null);

const productionForm = reactive({
  projetId: '',
  siteId: '',
  typeElementPrefabriqueId: '',
  quantiteProduite: '',
  dureeMaturationJours: '0',
  dateProduction: toDateInput(new Date())
});

const expeditionForm = reactive({
  projetId: '',
  siteId: '',
  dateExpedition: toDateInput(new Date()),
  idVehicule: '',
  nomChauffeur: '',
  statut: 'PLANIFIE'
});

const expeditionItems = ref<ExpeditionItemDraft[]>([
  { uid: 1, typeElementPrefabriqueId: '', quantiteEnvoyee: '' }
]);
const nextExpeditionItemUid = ref(2);

const fluxColumns: TableColumn[] = [
  { key: 'type', label: 'Type' },
  { key: 'designation', label: 'Désignation' },
  { key: 'quantiteCommandee', label: 'Commandée' },
  { key: 'quantiteProduite', label: 'Produite' },
  { key: 'resteAProduire', label: 'Reste' },
  { key: 'quantiteEnMaturation', label: 'Maturation' },
  { key: 'quantiteLivrableDate', label: 'Livrable date' },
  { key: 'quantiteStockUsine', label: 'Stock usine' },
  { key: 'quantiteExpediee', label: 'Expédiée' }
];

const lotColumns: TableColumn[] = [
  { key: 'dateProduction', label: 'Date' },
  { key: 'projet', label: 'Projet' },
  { key: 'site', label: 'Site' },
  { key: 'typeElement', label: 'Type' },
  { key: 'quantiteProduite', label: 'Qté produite' }
];

const filteredSites = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const scoped = projetId === null ? sites.value : sites.value.filter((site) => site.projetId === projetId);
  return [...scoped].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
});

const productionFormSites = computed(() => {
  const projetId = toNumberOrNull(productionForm.projetId);
  if (projetId === null) {
    return [] as Site[];
  }
  return sites.value.filter((site) => site.projetId === projetId);
});

const expeditionFormSites = computed(() => {
  const projetId = toNumberOrNull(expeditionForm.projetId);
  if (projetId === null) {
    return [] as Site[];
  }
  return sites.value.filter((site) => site.projetId === projetId);
});

const livrableByType = computed(() => {
  return new Map(
    livrablesProjection.value.map((item) => [item.typeElementPrefabriqueId, item.quantiteLivrable] as const)
  );
});

const fluxRows = computed<FluxRow[]>(() => {
  return fluxElements.value.map((row) => ({
    ...row,
    resteAProduire: Math.max(row.quantiteCommandee - row.quantiteProduite, 0),
    quantiteLivrableDate: livrableByType.value.get(row.typeElementPrefabriqueId) ?? row.quantiteStockUsine
  }));
});

const fluxRowsFiltered = computed(() => {
  const needle = searchText.value.trim().toLowerCase();
  if (!needle) {
    return fluxRows.value;
  }
  return fluxRows.value.filter((row) =>
    `${row.type} ${row.designation}`.toLowerCase().includes(needle)
  );
});

const lotsFiltered = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const siteId = toNumberOrNull(selectedSiteId.value);
  const needle = searchText.value.trim().toLowerCase();

  return lots.value.filter((lot) => {
    if (projetId !== null && lot.projetId !== projetId) {
      return false;
    }
    if (siteId !== null && lot.siteId !== siteId) {
      return false;
    }
    if (!needle) {
      return true;
    }
    const typeLabel = resolveTypeLabel(lot.typeElementPrefabriqueId).toLowerCase();
    const siteName = (lot.siteNom ?? '').toLowerCase();
    return typeLabel.includes(needle) || siteName.includes(needle);
  });
});

const kpis = computed(() => {
  return fluxRowsFiltered.value.reduce(
    (acc, row) => ({
      resteAProduire: acc.resteAProduire + row.resteAProduire,
      enMaturation: acc.enMaturation + row.quantiteEnMaturation,
      livrableDate: acc.livrableDate + row.quantiteLivrableDate,
      stockUsine: acc.stockUsine + row.quantiteStockUsine
    }),
    {
      resteAProduire: 0,
      enMaturation: 0,
      livrableDate: 0,
      stockUsine: 0
    }
  );
});

watch(
  () => selectedProjetId.value,
  () => {
    const projetId = toNumberOrNull(selectedProjetId.value);
    const siteId = toNumberOrNull(selectedSiteId.value);
    if (siteId !== null && !sites.value.some((site) => site.id === siteId && site.projetId === projetId)) {
      selectedSiteId.value = '';
    }
  }
);

watch(
  () => [selectedProjetId.value, selectedSiteId.value, horizonDate.value],
  () => {
    loadFluxData();
  }
);

watch(
  () => productionForm.projetId,
  () => {
    const projetId = toNumberOrNull(productionForm.projetId);
    const siteId = toNumberOrNull(productionForm.siteId);
    if (siteId !== null && !sites.value.some((site) => site.id === siteId && site.projetId === projetId)) {
      productionForm.siteId = '';
    }
  }
);

watch(
  () => expeditionForm.projetId,
  () => {
    const projetId = toNumberOrNull(expeditionForm.projetId);
    const siteId = toNumberOrNull(expeditionForm.siteId);
    if (siteId !== null && !sites.value.some((site) => site.id === siteId && site.projetId === projetId)) {
      expeditionForm.siteId = '';
    }
  }
);

onMounted(async () => {
  await loadProjetsAndSites();
  await Promise.all([loadTypesElements(), loadLots()]);

  if (!selectedProjetId.value && projets.value.length > 0) {
    selectedProjetId.value = String(projets.value[0].id);
  }

  await loadFluxData();
});

async function loadProjetsAndSites() {
  try {
    const response = await apiFetch('/projets');
    projets.value = (response as { items: Projet[] }).items || [];

    const siteRequests = projets.value.map(async (projet) => {
      const res = await apiFetch(`/projets/${projet.id}/sites`);
      const items = (res as { items: Array<{ id: number; nom: string }> }).items || [];
      return items.map((site) => ({
        id: site.id,
        nom: site.nom,
        projetId: projet.id
      })) satisfies Site[];
    });

    const groupedSites = await Promise.all(siteRequests);
    sites.value = groupedSites.flat();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des projets';
  }
}

async function loadTypesElements() {
  try {
    const response = await apiFetch('/types-elements-prefabriques');
    typesElements.value = (response as { items: TypeElementPrefabrique[] }).items || [];
  } catch (err) {
    console.error('Erreur lors du chargement des types:', err);
  }
}

async function loadLots() {
  loadingLots.value = true;
  try {
    const response = await apiFetch('/production/lots-production?limit=500&offset=0');
    lots.value = (response as { items: LotProduction[] }).items || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des lots';
  } finally {
    loadingLots.value = false;
  }
}

async function loadFluxData() {
  const projetId = toNumberOrNull(selectedProjetId.value);
  if (projetId === null) {
    fluxElements.value = [];
    livrablesProjection.value = [];
    return;
  }

  const siteId = toNumberOrNull(selectedSiteId.value);
  loadingFlux.value = true;
  try {
    const fluxParams = new URLSearchParams();
    if (siteId !== null) {
      fluxParams.set('siteId', String(siteId));
    }

    const livrableParams = new URLSearchParams({ dateCible: horizonDate.value });
    if (siteId !== null) {
      livrableParams.set('siteId', String(siteId));
    }

    const [fluxRes, livrableRes] = await Promise.all([
      apiFetch(`/metriques/projets/${projetId}/flux-elements${fluxParams.toString() ? `?${fluxParams.toString()}` : ''}`),
      apiFetch(`/projets/${projetId}/livrables?${livrableParams.toString()}`).catch(() => ({ items: [] }))
    ]);

    fluxElements.value = (fluxRes as { items: FluxElement[] }).items || [];
    livrablesProjection.value = (livrableRes as { items: LivrableProjection[] }).items || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement du flux usine';
    fluxElements.value = [];
    livrablesProjection.value = [];
  } finally {
    loadingFlux.value = false;
  }
}

function setHorizon(days: number) {
  horizonDate.value = toDateInput(addDays(new Date(), days));
}

function openProductionModal() {
  editingLot.value = null;
  productionForm.projetId = selectedProjetId.value;
  productionForm.siteId = selectedSiteId.value;
  productionForm.typeElementPrefabriqueId = '';
  productionForm.quantiteProduite = '';
  productionForm.dureeMaturationJours = '0';
  productionForm.dateProduction = toDateInput(new Date());
  productionFormError.value = null;
  showProductionModal.value = true;
}

function openEditLotModal(lot: LotProduction) {
  editingLot.value = lot;
  productionForm.projetId = String(lot.projetId);
  productionForm.siteId = lot.siteId ? String(lot.siteId) : '';
  productionForm.typeElementPrefabriqueId = String(lot.typeElementPrefabriqueId);
  productionForm.quantiteProduite = String(lot.quantiteProduite);
  productionForm.dureeMaturationJours = '0';
  productionForm.dateProduction = lot.dateProduction ?? toDateInput(new Date());
  productionFormError.value = null;
  showProductionModal.value = true;
}

function closeProductionModal() {
  showProductionModal.value = false;
  editingLot.value = null;
  productionFormError.value = null;
}

async function saveProduction() {
  const projetId = toNumberOrNull(productionForm.projetId);
  const typeElementPrefabriqueId = toNumberOrNull(productionForm.typeElementPrefabriqueId);
  const quantiteProduite = toNumberOrNull(productionForm.quantiteProduite);
  const dureeMaturationJours = toNumberOrNull(productionForm.dureeMaturationJours);
  const siteId = toNumberOrNull(productionForm.siteId);

  if (projetId === null || typeElementPrefabriqueId === null || quantiteProduite === null || dureeMaturationJours === null) {
    productionFormError.value = 'Veuillez renseigner les champs obligatoires.';
    return;
  }
  if (!productionForm.dateProduction) {
    productionFormError.value = 'La date de production est requise.';
    return;
  }

  savingProduction.value = true;
  productionFormError.value = null;

  try {
    const payload = {
      siteId,
      typeElementPrefabriqueId,
      quantiteProduite,
      dureeMaturationJours,
      dateProduction: productionForm.dateProduction
    };

    if (editingLot.value) {
      await apiFetch(`/production/lots-production/${editingLot.value.id}`, {
        method: 'PATCH',
        body: payload
      });
    } else {
      await apiFetch(`/projets/${projetId}/lots-production`, {
        method: 'POST',
        body: payload
      });
    }

    closeProductionModal();
    await Promise.all([loadLots(), loadFluxData()]);
  } catch (err) {
    productionFormError.value = err instanceof Error ? err.message : 'Erreur lors de l’enregistrement';
  } finally {
    savingProduction.value = false;
  }
}

function openExpeditionModal() {
  expeditionForm.projetId = selectedProjetId.value;
  expeditionForm.siteId = selectedSiteId.value;
  expeditionForm.dateExpedition = toDateInput(new Date());
  expeditionForm.idVehicule = '';
  expeditionForm.nomChauffeur = '';
  expeditionForm.statut = 'PLANIFIE';
  expeditionItems.value = [{ uid: 1, typeElementPrefabriqueId: '', quantiteEnvoyee: '' }];
  nextExpeditionItemUid.value = 2;
  expeditionFormError.value = null;
  showExpeditionModal.value = true;
}

function closeExpeditionModal() {
  showExpeditionModal.value = false;
  expeditionFormError.value = null;
}

function addExpeditionItemRow() {
  expeditionItems.value.push({
    uid: nextExpeditionItemUid.value++,
    typeElementPrefabriqueId: '',
    quantiteEnvoyee: ''
  });
}

function removeExpeditionItemRow(uid: number) {
  expeditionItems.value = expeditionItems.value.filter((item) => item.uid !== uid);
}

async function saveExpedition() {
  const projetId = toNumberOrNull(expeditionForm.projetId);
  const siteId = toNumberOrNull(expeditionForm.siteId);
  if (projetId === null || siteId === null) {
    expeditionFormError.value = 'Projet et site sont obligatoires.';
    return;
  }
  if (!expeditionForm.dateExpedition) {
    expeditionFormError.value = 'La date d’expédition est requise.';
    return;
  }

  savingExpedition.value = true;
  expeditionFormError.value = null;
  try {
    const createPayload: {
      siteId: number;
      dateExpedition: string;
      statut: string;
      idVehicule?: string;
      nomChauffeur?: string;
    } = {
      siteId,
      dateExpedition: expeditionForm.dateExpedition,
      statut: expeditionForm.statut
    };

    if (expeditionForm.idVehicule) {
      createPayload.idVehicule = expeditionForm.idVehicule;
    }
    if (expeditionForm.nomChauffeur) {
      createPayload.nomChauffeur = expeditionForm.nomChauffeur;
    }

    const created = await apiFetch(`/projets/${projetId}/expeditions`, {
      method: 'POST',
      body: createPayload
    }) as { id: number };

    const validItems = expeditionItems.value
      .map((item) => ({
        typeElementPrefabriqueId: toNumberOrNull(item.typeElementPrefabriqueId),
        quantiteEnvoyee: toNumberOrNull(item.quantiteEnvoyee)
      }))
      .filter(
        (item): item is { typeElementPrefabriqueId: number; quantiteEnvoyee: number } =>
          item.typeElementPrefabriqueId !== null && item.quantiteEnvoyee !== null && item.quantiteEnvoyee > 0
      );

    await Promise.all(
      validItems.map((item) =>
        apiFetch(`/expeditions/${created.id}/items`, {
          method: 'POST',
          body: item
        })
      )
    );

    closeExpeditionModal();
    await loadFluxData();
  } catch (err) {
    expeditionFormError.value = err instanceof Error ? err.message : 'Erreur lors de la création de la livraison';
  } finally {
    savingExpedition.value = false;
  }
}

function resolveTypeLabel(typeId: number): string {
  const found = typesElements.value.find((type) => type.id === typeId);
  return found ? `${found.libelle} (${found.code})` : `Type #${typeId}`;
}

function viewLot(lot: LotProduction) {
  router.push({ name: 'production-detail', params: { id: lot.id } });
}

function toNumberOrNull(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR');
}

function toDateInput(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(base: Date, days: number) {
  const copy = new Date(base.getTime());
  copy.setDate(copy.getDate() + days);
  return copy;
}
</script>

<style scoped>
.usine-page {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
}

.page-header h1 {
  margin: 0;
}

.page-actions {
  display: flex;
  gap: var(--spacing-sm);
}

.filters-card {
  padding: var(--spacing-md);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--spacing-md);
}

.horizon-actions {
  margin-top: var(--spacing-sm);
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
}

.kpis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: var(--spacing-md);
}

.kpi-card {
  padding: var(--spacing-lg);
  text-align: center;
}

.kpi-value {
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.kpi-label {
  margin-top: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.panel-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-header h2 {
  margin: 0;
}

.error {
  padding: var(--spacing-md);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.form-error {
  padding: var(--spacing-sm);
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  background: #fee;
  color: #c33;
  font-size: var(--font-size-sm);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
}

.items-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.items-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
}

.item-row {
  display: grid;
  grid-template-columns: 2fr 1fr auto;
  gap: var(--spacing-sm);
  align-items: end;
}

@media (max-width: 900px) {
  .item-row {
    grid-template-columns: 1fr;
  }
}
</style>
